import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ToolGlyph } from "./desk-tool-glyph.js";
import { Lettering } from "../ui/primitives.js";

/**
 * "ARRANGE BY." (2026-10-31, Andre, from the arrange-files ideation): the
 * desk's other arrangements, behind one word in its heading beside "tidy.".
 * A menu, not a mode: choosing an item does that arrangement once and the
 * menu closes; nothing is remembered as "sorted by", and the papers stay
 * free to move. The word is a menu button in the WAI-ARIA sense — it says it
 * has a menu and whether the menu is open; opening puts the focus on the
 * first choice; the arrows walk the choices (and wrap), Home and End jump,
 * Escape closes and gives the focus back to the word, Tab closes and moves
 * on, a press anywhere else closes. The choices' words and hints come from
 * the copy with the rest of the desk's; this file only lays them out.
 */
export interface ArrangeChoice {
  readonly id: string;
  readonly word: string;
  readonly hint: string;
}

export function ArrangeMenu({ word, hint, choices, onChoose }: { word: string; hint: string; choices: readonly ArrangeChoice[]; onChoose: (id: string) => void }): ReactNode {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const items = (): HTMLButtonElement[] => [...(rootRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
  const close = useCallback((returnFocus: boolean): void => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);
  // A press anywhere outside the control closes it — on the way down, before
  // whatever was pressed (a card, say) takes the pointer for itself.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const root = rootRef.current;
      if (root !== null && event.target instanceof Node && !root.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => { document.removeEventListener("pointerdown", onPointerDown, true); };
  }, [open]);
  // Opening puts the focus on the first choice.
  useEffect(() => {
    if (open) items()[0]?.focus();
  }, [open]);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      if (!open) return;
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (!open) return;
    if (event.key === "Tab") { setOpen(false); return; }
    const all = items();
    if (all.length === 0) return;
    const at = all.findIndex((item) => item === document.activeElement);
    const to = (index: number): void => { event.preventDefault(); all[(index + all.length) % all.length]?.focus(); };
    if (event.key === "ArrowDown") to(at + 1);
    else if (event.key === "ArrowUp") to(at - 1);
    else if (event.key === "Home") to(0);
    else if (event.key === "End") to(all.length - 1);
  };
  return (
    <div ref={rootRef} className="pcs-desk-menu-root" onKeyDown={onKeyDown}>
      <button ref={buttonRef} type="button" data-testid="desk-arrange-by" title={hint} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => { setOpen((was) => !was); }}>
        <Lettering text={word} />
        <ToolGlyph of="menu" />
      </button>
      {open ? (
        <div id={menuId} role="menu" aria-label={word} data-testid="desk-arrange-menu" className="pcs-desk-menu">
          {choices.map((choice) => (
            <button key={choice.id} type="button" role="menuitem" data-testid={`desk-arrange-${choice.id}`} title={choice.hint} onClick={() => { onChoose(choice.id); close(true); }}>
              <Lettering text={choice.word} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
